"use client"

import { HomeIcon, Sparkles, ArrowRight } from "lucide-react"
import Link from "next/link"

import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator } from "@/core/ui/breadcrumb"
import { JsonLd } from "@/core/ui/json-ld"
import CategorySelector from "@/modules/marketing/services/components/category-selector"
import EngagementModels from "@/modules/marketing/services/components/engagement-models"
import MethodologyStack from "@/modules/marketing/services/components/methodology-stack"
import ServicesHero from "@/modules/marketing/services/components/services-hero"
import TechGravity from "@/modules/marketing/services/components/tech-gravity"

export default function ServicesPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Technical Leadership & Development Services",
    "serviceType": "Software Engineering Services",
    "provider": {
      "@type": "ProfessionalService",
      "name": "Hamid Ayub",
      "url": "https://hamidayub.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Lahore",
        "addressCountry": "Pakistan"
      }
    },
    "areaServed": "Worldwide",
    "audience": {
      "@type": "Audience",
      "audienceType": "Investors, Entrepreneurs, CTOs, Project Managers, Business Directors"
    },
    "description": "Premium digital services including AI Integration, Web3 AI Solutions, and Agentic Automation. Based in Lahore, Pakistan with international quality standards.",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Professional Services",
      "itemListElement": [
        { "@type": "Offer", "name": "Discovery Call", "price": "200", "priceCurrency": "USD", "url": "https://hamidayub.com/contact?service=discovery-call" },
        { "@type": "Offer", "name": "AI System Architecture", "price": "1500", "priceCurrency": "USD", "url": "https://hamidayub.com/contact?service=ai-system-architecture" },
        { "@type": "Offer", "name": "Agentic Workflow Development", "price": "2500", "priceCurrency": "USD", "url": "https://hamidayub.com/contact?service=agentic-workflow-development" },
        { "@type": "Offer", "name": "RAG Assistant POC", "price": "5000", "priceCurrency": "USD", "url": "https://hamidayub.com/contact?service=rag-assistant" },
        { "@type": "Offer", "name": "AI Integration Development", "price": "15000", "priceCurrency": "USD", "url": "https://hamidayub.com/contact?service=ai-integration" }
      ]
    }
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://hamidayub.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://hamidayub.com/services"
      }
    ]
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How much does an AI Integration project cost?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "AI system development and architecture starts at $1,500 for a focused strategy and data readiness review. Agentic workflow and custom AI agent developments start at $2,500 due to the complexity of the orchestration. A Discovery Call ($200) determines the exact scope for your situation."
        }
      },
      {
        "@type": "Question",
        "name": "What is a Principal Engineer and how does it relate to AI Integration?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A Principal Engineer provides hands-on technical leadership and architecture — not just basic integration. I architect complex AI systems, write critical-path code, ensure your data pipelines are secure, and deploy agentic workflows that scale with your business."
        }
      },
      {
        "@type": "Question",
        "name": "How does the Engineering Partnership subscription work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The Engineering Partnership is a fixed-rate monthly subscription ($3,000/month) that gives you dedicated access to a Principal-level architect. One price, unlimited requests, predictable execution. Includes architecture design, code review, technical strategy, and hands-on development — whatever the situation requires."
        }
      },
      {
        "@type": "Question",
        "name": "Do you work with international clients or only in Pakistan?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Based in Lahore, Pakistan. Working with clients worldwide — primarily US, UK, UAE, and European markets. All engagement is remote-first with overlap in US and European business hours. The principal advantage: elite execution quality at competitive economics."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-black text-white selection:bg-primary/30 scroll-smooth">
      <JsonLd data={jsonLd} />
      <JsonLd data={breadcrumbJsonLd} />
      <JsonLd data={faqJsonLd} />

      {/* Navigation Breadcrumb - Absolute positioned over hero */}
      <div className="absolute top-24 left-0 w-full z-20 px-4 pointer-events-none">
        <div className="container mx-auto pointer-events-auto">
          <Breadcrumb className="text-muted-foreground/40 hover:text-white/80 transition-colors inline-block backdrop-blur-xs px-4 py-2 rounded-full border border-white/5 bg-black/20">
            <BreadcrumbItem>
              <BreadcrumbLink href="/" className="hover:text-white transition-colors">
                <HomeIcon className="mr-2 h-3 w-3" />
                Home
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/services" className="text-white font-medium">Services</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
        </div>
      </div>

      <ServicesHero />
      
      {/* Targeted CTA for Partnership */}
      <div className="container mx-auto px-6 -mt-12 mb-12 relative z-20">
        <Link href="/services/partnership" className="group block max-w-lg mx-auto p-1 rounded-2xl bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-white/5 hover:border-cyan-500/30 transition-all">
          <div className="bg-black/80 backdrop-blur-xl p-4 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2 rounded-lg bg-cyan-500/10 text-cyan-400">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-white">The Engineering Partnership</h4>
                <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Dedicated Principal Execution</p>
              </div>
            </div>
            <ArrowRight className="h-4 w-4 text-cyan-400 group-hover:translate-x-1 transition-transform" />
          </div>
        </Link>
      </div>

      <MethodologyStack />
      <CategorySelector />
      <EngagementModels />
      <TechGravity />

    </div>
  )
}
